<template>
   <transition name="modal-fade" v-if="isModalOpen">
      <div :class="['gts-modal-wrapper', type ? `modal-${type}` : '']">
         <div class="gts-modal-inner backdrop-wrapper">
            <div :class="`modal-content-wrapper modal-${size}`" @click.stop>
               <span class="btn_modalClose" @click="closeModal">
                  <i class="gts-icon icon-close-rounded"></i>
               </span>
               <header class="modal-content-header" v-if="video?.title">
                  <h4>{{ video.title }}</h4>
                  <span>{{ video.description }}</span>
                  <slot name="headerCTA"></slot>
               </header>
               <section class="modal-content-body">
                  <template v-if="type === 'media' && video?.videoUrl">
                     <!-- Video Loading -->
                     <div v-if="isVideoLoading" class="video-loading-container">
                        <div class="video-loading-spinner"></div>
                        <p>{{ $t("uiModal_lblVideoLoading") }}</p>
                     </div>

                     <!-- Video Player -->
                     <video
                        v-show="!isVideoLoading"
                        autoplay
                        loop
                        muted
                        playsinline
                        controls
                        style="width: 100%; border-radius: 12px"
                        @loadstart="handleVideoLoadStart"
                        @canplay="handleVideoCanPlay"
                        @loadeddata="handleVideoCanPlay"
                        @error="handleVideoError">
                        <source :src="video.videoUrl" type="video/mp4" />
                     </video>

                     <!-- Error State -->
                     <div v-if="videoError" class="video-error-container">
                        <i class="gts-icon icon-warning"></i>
                        <p>error.occured</p>
                     </div>
                  </template>
                  <slot v-else />
               </section>
               <footer class="modal-content-footer" v-if="type != 'media'">
                  <slot name="footer"></slot>
               </footer>
            </div>
         </div>
      </div>
   </transition>
</template>

<script>
export default {
   name: "UIModal",
   props: {
      size: { type: String },
      title: { type: String },
      type: { type: String },
      video: { type: Object },
   },
   data() {
      return {
         isModalOpen: false,
         isVideoLoading: true,
         videoError: false,
      };
   },
   methods: {
      closeModal() {
         this.isModalOpen = false;
         this.$emit("modalClosed");
         // Reset video states when modal closes
         this.resetVideoStates();
      },
      triggerClickOutSide() {
         this.isModalOpen = false;
         this.$emit("modalClosed");
         this.resetVideoStates();
      },
      handleVideoLoadStart() {
         this.isVideoLoading = true;
         this.videoError = false;
      },
      handleVideoCanPlay() {
         this.isVideoLoading = false;
         this.videoError = false;
      },
      handleVideoError() {
         this.isVideoLoading = false;
         this.videoError = true;
      },

      resetVideoStates() {
         this.isVideoLoading = true;
         this.videoError = false;
      },
      // Public method to open modal
      openModal() {
         this.isModalOpen = true;
         if (this.type === "media" && this.video?.videoUrl) {
            this.resetVideoStates();
         }
      },
   },
   watch: {
      // Reset loading when video URL changes
      "video.videoUrl"() {
         if (this.type === "media") {
            this.resetVideoStates();
         }
      },
   },
};
</script>

<style>
.modal-fade-enter-active,
.modal-fade-leave-active {
   transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
   opacity: 0;
}
</style>

<style lang="scss" scoped>
html body .gts-modal-wrapper {
   position: fixed;
   width: 100%;
   height: 100%;
   top: 0;
   left: 0;
   z-index: 999;

   &.modal-media {
      .gts-modal-inner {
         .backdrop-wrapper {
            background-color: rgba(0, 0, 0, 0.7) !important;
         }
         .modal-content-wrapper {
            padding: 1rem;
            padding-top: 54px;

            video {
               max-height: 80vh;
               width: auto;
               height: auto;
               display: block;
               margin: 0 auto;
            }
         }
      }
   }

   .gts-modal-inner {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;

      &.backdrop-wrapper {
         background-color: rgba(0, 0, 0, 0.5);
      }

      .modal-content-wrapper {
         position: relative;
         padding: 48px;
         background-color: white;
         border-radius: 16px;
         box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);

         .btn_modalClose {
            position: absolute;
            top: 16px;
            right: 16px;
            cursor: pointer;
            z-index: 10;
         }

         .modal-content-header {
            margin-bottom: 16px;
            padding-bottom: 16px;
            border-bottom: 1px solid #dee2e6;

            h4 {
               font-size: 24px;
               font-weight: 500;
            }
         }

         .modal-content-body {
            // Video loading styles
            .video-loading-container {
               display: flex;
               flex-direction: column;
               align-items: center;
               justify-content: center;
               min-height: 200px;
               color: #666;
               text-align: center;

               p {
                  margin: 16px 0 0 0;
                  font-size: 16px;
               }
            }

            .video-loading-spinner {
               width: 40px;
               height: 40px;
               border: 4px solid #f3f3f3;
               border-top: 4px solid var(--primary);
               border-radius: 50%;
               animation: spin 1s linear infinite;
            }

            .video-error-container {
               display: flex;
               flex-direction: column;
               align-items: center;
               justify-content: center;
               min-height: 200px;
               color: #dc3545;
               text-align: center;

               i {
                  font-size: 48px;
                  margin-bottom: 16px;
               }

               p {
                  margin: 0 0 16px 0;
                  font-size: 16px;
               }

               .retry-btn {
                  background-color: #007bff;
                  color: white;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                  transition: background-color 0.2s;

                  &:hover {
                     background-color: #0056b3;
                  }
               }
            }
         }

         .modal-content-footer {
            border-top: 1px solid #dee2e6;
            padding-top: 16px;
            margin-top: 16px;

            &::v-deep .customer-book-form-cta {
               display: flex;
               justify-content: flex-end;
               align-items: center;

               & > button:first-child {
                  margin-right: 16px;
               }
            }
         }

         &.modal-lg {
            width: 90%;
            max-width: 90%;
         }
         &.modal-md {
            width: 40%;
            max-width: 40%;

            @media (max-width: 900px) {
               width: 90%;
               max-width: 90%;
            }
            @media (max-width: 1200px) {
               width: 96%;
               max-width: 96%;
            }
         }
         &.modal-sm {
            width: 30%;
            max-width: 30%;
         }
      }
   }
}

@keyframes spin {
   0% {
      transform: rotate(0deg);
   }
   100% {
      transform: rotate(360deg);
   }
}
</style>
